import PrintIcon from "@mui/icons-material/Print";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";

import { BackdropCustom } from "../backdrop/Backdrop";
import { useToastr } from "../toast/Toast";
import api from "../../services/api";
import { downloadBase64Pdf } from "../../utils/downloadPdf";
import { getUtcOffset } from "../../utils/timezone";

interface IPrintCollectorsPdfDialogProps {
  /** Se true, exibe o botão que abre o diálogo */
  showButton?: boolean;
  /** Label do botão gatilho */
  buttonLabel?: string;
  /** O diálogo é controlado externamente (sem botão interno) */
  open?: boolean;
  /** Callback externo para fechar o diálogo */
  onClose?: () => void;
  /** ID da exposição para o endpoint */
  exhibitionId: string;
}

export const PrintCollectorsPdfDialog: React.FC<
  IPrintCollectorsPdfDialogProps
> = ({
  showButton = false,
  buttonLabel = "Relatório de Colecionadores",
  open: externalOpen,
  onClose: externalOnClose,
  exhibitionId,
}) => {
  const toastr = useToastr();
  const [internalOpen, setInternalOpen] = useState(false);
  const [onlyReceived, setOnlyReceived] = useState(true);
  const [loading, setLoading] = useState(false);

  // Suporte a uso controlado externamente ou via botão interno
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;

  const handleOpen = () => {
    setOnlyReceived(true);
    setInternalOpen(true);
  };

  const handleClose = () => {
    if (externalOnClose) externalOnClose();
    setInternalOpen(false);
  };

  const handleGenerate = async () => {
    if (!exhibitionId) {
      toastr.error("Exposição não encontrada");
      return;
    }

    handleClose();
    setLoading(true);
    try {
      const response = await api.get(
        `/collector/plants/report/exhibition/${exhibitionId}/collectors`,
        {
          params: {
            onlyReceived,
            utcOffset: getUtcOffset(),
          },
        },
      );

      const { filename, base64 } = response.data;

      if (!base64 || !filename) {
        toastr.error("Nenhum relatório encontrado para gerar");
        return;
      }

      downloadBase64Pdf(base64, filename);
      toastr.success("Relatório gerado com sucesso!");
    } catch (error: any) {
      toastr.error(
        error?.response?.data?.message ||
          "Erro ao gerar relatório de colecionadores",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showButton && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={handleOpen}
          disabled={!exhibitionId}
          size="small"
        >
          {buttonLabel}
        </Button>
      )}

      <Dialog
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="print-collectors-pdf-dialog-title"
        aria-describedby="print-collectors-pdf-dialog-description"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <DialogTitle id="print-collectors-pdf-dialog-title">
          Gerar relatório de colecionadores
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="print-collectors-pdf-dialog-description"
            sx={{ mb: 2 }}
          >
            Selecione se deseja listar apenas os colecionadores com plantas
            recebidas na exposição.
          </DialogContentText>
          <Tooltip
            title="Se marcado, exibe apenas os colecionadores com plantas recebidas."
            arrow
            placement="right"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyReceived}
                  onChange={e => setOnlyReceived(e.target.checked)}
                  color="primary"
                />
              }
              label="Apenas colecionadores com plantas recebidas"
            />
          </Tooltip>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleGenerate}
            color="primary"
            variant="contained"
            autoFocus
          >
            Gerar Relatório
          </Button>
        </DialogActions>
      </Dialog>

      {loading && <BackdropCustom />}
    </>
  );
};
